// @ts-nocheck
import { Box, Skeleton } from '@mui/material';
import { WarningCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XGInput } from 'xg-cl';
import {
  getSecondaryNotificationEmail,
  updateSecondaryNotificationEmail
} from '../../../services/tenant/tenant';
import { ErrorToast } from '../../../utils/alerts';
import { isEmailValid } from '../../../utils/emailValidator';
import PropTypes from 'prop-types';
import './AccountSettingsPanel.css';

const AccountSettingsPanel = ({ id }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [savedSecondaryEmail, setSavedSecondaryEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchAccountSettings = async () => {
      setIsLoading(true);

      try {
        const response = await getSecondaryNotificationEmail();
        const currentSecondaryEmail = response?.data?.secondaryEmail || '';

        setSecondaryEmail(currentSecondaryEmail);
        setSavedSecondaryEmail(currentSecondaryEmail);
      } catch (error) {
        ErrorToast(t('common:errorLoadingSettings'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountSettings();
  }, [t]);

  const handleSecondaryEmailChange = (event) => {
    setSecondaryEmail(event.target.value);
    setErrorMessage('');
  };

  const handleSecondaryEmailBlur = async () => {
    if (isSaving) {
      return;
    }

    const nextSecondaryEmail = secondaryEmail.trim();

    if (nextSecondaryEmail === savedSecondaryEmail) {
      return;
    }

    if (nextSecondaryEmail && !isEmailValid(nextSecondaryEmail)) {
      setErrorMessage(t('settings:invalidSecondaryNotificationEmail'));
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateSecondaryNotificationEmail({
        secondaryEmail: nextSecondaryEmail
      });

      if (!response?.data || response?.error) {
        throw new Error('Failed to save secondary notification email.');
      }

      const savedEmail = response.data.secondaryEmail || '';

      setSecondaryEmail((currentSecondaryEmail) =>
        currentSecondaryEmail.trim() === nextSecondaryEmail
          ? savedEmail
          : currentSecondaryEmail
      );
      setSavedSecondaryEmail(savedEmail);
    } catch (error) {
      setSecondaryEmail((currentSecondaryEmail) =>
        currentSecondaryEmail.trim() === nextSecondaryEmail
          ? savedSecondaryEmail
          : currentSecondaryEmail
      );
      ErrorToast(t('common:errorSavingSettings'));
    } finally {
      setIsSaving(false);
    }
  };

  const helperId = 'global-account-settings-secondary-email-helper';
  const errorId = 'global-account-settings-secondary-email-error';
  const describedBy = [helperId, errorMessage ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      id={id}
      className='global-account-settings-panel'
      data-testid='account-settings-panel'
    >
      {isLoading ? (
        <Box className='global-account-settings-panel__content'>
          <Skeleton width='28%' height={24} />
          <Skeleton width='65%' height={18} />
          <Skeleton variant='rectangular' width='100%' height={78} />
        </Box>
      ) : (
        <Box className='global-account-settings-panel__content'>
          <Box className='global-account-settings-panel__header'>
            <h6 className='text-lg-semibold global-account-settings-panel__title'>
              {t('settings:accountSettings')}
            </h6>
            <p className='text-sm-regular global-account-settings-panel__description'>
              {t('settings:accountSettingsHelperText')}
            </p>
          </Box>

          <Box className='global-account-settings-panel__field'>
            <XGInput
              id='global-account-settings-secondary-email-input'
              name='secondaryNotificationEmail'
              type='email'
              label={t('settings:secondaryNotificationEmail')}
              placeholder={t('settings:enterSecondaryNotificationEmail')}
              value={secondaryEmail}
              onChange={handleSecondaryEmailChange}
              onBlur={handleSecondaryEmailBlur}
              error={Boolean(errorMessage)}
              hint={errorMessage}
              icon={Boolean(errorMessage)}
              customIcon={
                errorMessage ? (
                  <WarningCircle color='#F04438' size={16} />
                ) : null
              }
              inputProps={{
                'aria-describedby': describedBy,
                'aria-invalid': Boolean(errorMessage)
              }}
            />
            <p
              id={helperId}
              className='text-sm-regular global-account-settings-panel__helper-text'
            >
              {t('settings:secondaryNotificationEmailHelperText')}
            </p>
            {errorMessage ? (
              <span
                id={errorId}
                className='global-account-settings-panel__sr-only'
              >
                {errorMessage}
              </span>
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
};

AccountSettingsPanel.propTypes = {
  id: PropTypes.string.isRequired
};

export default AccountSettingsPanel;
